import { isManager } from "@/lib/access";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActiveSession } from "@/context/ActiveSessionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Play, Pause, Paperclip, Plus, Download, Trash2, Send, CheckCircle2, PauseCircle, Timer, Pencil } from "lucide-react";
import { NewTaskDialog } from "./Tasks";

const STATUS_STYLES = {
  pending: "bg-slate-100 text-slate-600 border-slate-200",
  attended: "bg-sky-50 text-sky-700 border-sky-200",
  "in-progress": "bg-amber-50 text-amber-800 border-amber-200",
  awaiting: "bg-violet-50 text-violet-700 border-violet-200",
  "on-hold": "bg-orange-50 text-orange-700 border-orange-200",
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const fmtDuration = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const EVENT_META = {
  created: { label: "Created task", dot: "bg-emerald-500" },
  sub_task_created: { label: "Added sub-task", dot: "bg-indigo-500" },
  reassigned: { label: "Reassigned", dot: "bg-amber-500" },
  status_changed: { label: "Status changed", dot: "bg-slate-500" },
  awaiting: { label: "Marked awaiting", dot: "bg-violet-500" },
  on_hold: { label: "Put on hold", dot: "bg-orange-500" },
  done: { label: "Marked done", dot: "bg-emerald-600" },
  reopened: { label: "Reopened", dot: "bg-sky-500" },
};

function eventDescription(ev) {
  const m = ev.meta || {};
  switch (ev.type) {
    case "created":
      return m.assignee_name ? `assigned to ${m.assignee_name}` : "self-assigned";
    case "sub_task_created":
      return m.sub_task_name ? `“${m.sub_task_name}”` : "";
    case "reassigned":
      return `${m.from_name || "Unassigned"} → ${m.to_name || "Unassigned"}${m.bulk ? " (bulk)" : ""}`;
    case "awaiting":
    case "on_hold":
      return m.reason ? `reason: ${m.reason}` : "";
    case "done":
      return "";
    case "status_changed":
      return `${m.from || "—"} → ${m.to || "—"}`;
    default:
      return "";
  }
}

function TimelineRow({ ev }) {
  const meta = EVENT_META[ev.type] || { label: ev.type, dot: "bg-slate-400" };
  const desc = eventDescription(ev);
  return (
    <li data-testid={`timeline-${ev.id}`} className="px-5 py-2.5 flex items-start gap-3 text-sm">
      <span className={`mt-1.5 inline-block w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
      <div className="min-w-0 flex-1">
        <div className="text-slate-800">
          <b className="font-medium">{ev.actor_name}</b> · <span className="text-slate-600">{meta.label.toLowerCase()}</span>
          {desc && <span className="text-slate-500"> — {desc}</span>}
        </div>
        <div className="text-[11px] text-slate-400 mono">
          {ev.created_at?.slice(0, 16).replace("T", " ")}
        </div>
      </div>
    </li>
  );
}

export default function TaskDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { session: activeSession, notifyStarted, notifyStopped, openStopModal, openEndSegmentModal } = useActiveSession();
  const isThisRunning = !!(activeSession && activeSession.kind === "task" && activeSession.task_id === id);
  const isAnotherRunning = !!(activeSession && activeSession.kind === "task" && activeSession.task_id && activeSession.task_id !== id);
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [assignables, setAssignables] = useState([]);
  const [types, setTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [entities, setEntities] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [subOpen, setSubOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionOpen, setActionOpen] = useState(null); // "awaiting" | "on-hold" | "done"
  const [reason, setReason] = useState("");
  const [tick, setTick] = useState(0);
  const fileRef = useRef(null);
  const timerAnchor = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, cm, tl, sub, att, a, ty, sc, en] = await Promise.all([
        api.get(`/tasks/${id}`),
        api.get(`/tasks/${id}/comments`),
        api.get(`/tasks/${id}/timeline`),
        api.get(`/tasks?parent_id=${id}&view=all`),
        api.get(`/tasks/${id}/attachments`),
        api.get("/users/assignable"),
        api.get("/task-types"),
        api.get("/service-categories"),
        api.get("/entities"),
      ]);
      setTask(t.data);
      setComments(cm.data);
      setTimeline(tl.data);
      setSubtasks(sub.data);
      setAttachments(att.data);
      setAssignables(a.data);
      setTypes(ty.data);
      setServices(sc.data);
      setEntities(en.data);
      // mark opened
      try { await api.post(`/tasks/${id}/open`); } catch (e) { console.warn("Mark-opened failed:", e); }
    } catch (e) {
      toast.error(formatErr(e));
      nav("/tasks");
    } finally { setLoading(false); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  // Heartbeat: send dwell 30s every 30s while page open (only counts if current user is assignee).
  useEffect(() => {
    const iv = setInterval(async () => {
      try { await api.post(`/tasks/${id}/dwell?seconds=30`); } catch (e) { console.warn("Dwell heartbeat failed:", e); }
    }, 30000);
    return () => clearInterval(iv);
  }, [id]);

  // Running timer ticker (updates local display every second while THIS task is running)
  useEffect(() => {
    if (!task?.timer_started_at) return;
    timerAnchor.current = new Date(task.timer_started_at).getTime();
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [task?.timer_started_at]);

  // Cross-tab / global sync: if the active session refers to THIS task but the local task doc
  // is stale (e.g. started from another tab), reload so timer_started_at reflects DB truth.
  useEffect(() => {
    if (!task) return;
    const contextSaysRunning = !!activeSession && activeSession.task_id === id;
    const localSaysRunning = !!task.timer_started_at;
    if (contextSaysRunning !== localSaysRunning) {
      load();
    }
    // eslint-disable-next-line
  }, [activeSession?.session_id]);

  const liveSeconds = () => {
    if (!task) return 0;
    let base = task.total_seconds || 0;
    if (task.timer_started_at) {
      base += Math.floor((Date.now() - new Date(task.timer_started_at).getTime()) / 1000);
    }
    return base;
  };

  const toggleTimer = async () => {
    // If a task is running → open the GLOBAL "What next?" modal (Break/Meeting/Call/Sign-off).
    // Otherwise start the timer directly.
    if (task.timer_started_at) {
      openStopModal();
      return;
    }
    try {
      const { data } = await api.post(`/tasks/${id}/timer/start`);
      setTask({ ...task, timer_started_at: data.timer_started_at, total_seconds: data.total_seconds });
      toast.success("Timer started"); notifyStarted();
    } catch (e) {
      const active = e?.response?.data?.detail?.active;
      if (e?.response?.status === 409 && active) {
        toast.error(`Another task is already running: ${active.task_name}`);
        notifyStarted();
        return;
      }
      toast.error(formatErr(e));
    }
  };

  const goToActiveTask = () => {
    if (activeSession) nav(`/tasks/${activeSession.task_id}`);
  };

  const isPausedForSegment = !!(
    activeSession &&
    ["break", "meeting", "call"].includes(activeSession.kind) &&
    activeSession.previous_task_id === id
  );

  const sendComment = async () => {
    if (!message.trim()) return;
    try {
      const { data } = await api.post(`/tasks/${id}/comments`, { message: message.trim() });
      setComments([...comments, data]);
      setMessage("");
      // Reload task in case status changed
      const { data: t } = await api.get(`/tasks/${id}`);
      setTask(t);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submitAction = async () => {
    if (!actionOpen) return;
    if ((actionOpen === "awaiting" || actionOpen === "on-hold") && !reason.trim()) {
      toast.error("A reason is required"); return;
    }
    try {
      const { data } = await api.post(`/tasks/${id}/action`, { action: actionOpen, reason: reason.trim() });
      setTask(data);
      toast.success(`Marked ${actionOpen}`);
      setActionOpen(null); setReason("");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    try {
      await api.post(`/tasks/${id}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      const [att, t] = await Promise.all([api.get(`/tasks/${id}/attachments`), api.get(`/tasks/${id}`)]);
      setAttachments(att.data); setTask(t.data);
    } catch (er) { toast.error(formatErr(er)); }
    e.target.value = "";
  };

  const downloadAtt = async (att) => {
    try {
      const resp = await api.get(`/task-attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteAtt = async (attId) => {
    try {
      await api.delete(`/task-attachments/${attId}`);
      const [att, t] = await Promise.all([api.get(`/tasks/${id}/attachments`), api.get(`/tasks/${id}`)]);
      setAttachments(att.data); setTask(t.data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteTask = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tasks/${id}`);
      toast.success("Task deleted");
      nav(task.parent_id ? `/tasks/${task.parent_id}` : "/tasks");
    } catch (e) {
      toast.error(formatErr(e));
      setDeleting(false);
    }
  };

  if (loading || !task) return <div className="text-sm text-slate-500">Loading task…</div>;

  const canManage = task.creator_id === user?.id || isManager(user);
  const isAssignee = task.assignee_id === user?.id;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={() => nav("/tasks")} className="gap-1"><ArrowLeft className="w-4 h-4" /> Back to tasks</Button>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button data-testid="task-edit-btn" variant="outline" size="sm" onClick={() => setEditOpen(true)} className="gap-1.5">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Button>
            <Button data-testid="task-delete-btn" variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center gap-2 mb-2">
              <span data-testid="task-detail-status" className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${STATUS_STYLES[task.status]}`}>{task.status.replace("-", " ")}</span>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-50 border border-slate-200 text-slate-600">{task.task_type}</span>
              {task.service_category && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-50 border border-slate-200 text-slate-600">{task.service_category}</span>}
              {task.depth === 0 ? (
                <span data-testid="task-detail-level" className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-100 border border-slate-200 text-slate-700">Main task</span>
              ) : task.depth === 1 ? (
                <span data-testid="task-detail-level" className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-indigo-50 border border-indigo-200 text-indigo-700">Sub-task</span>
              ) : (
                <span data-testid="task-detail-level" className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-violet-50 border border-violet-200 text-violet-700">Sub-sub-task</span>
              )}
              {task.due_date && task.is_active !== false && task.due_date < new Date().toISOString().slice(0, 10) && (
                <span data-testid="task-detail-overdue" className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-rose-50 border border-rose-200 text-rose-700">Overdue</span>
              )}
              {task.recurrence && task.recurrence !== "none" && (
                <span data-testid="task-detail-recurrence" className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-teal-50 border border-teal-200 text-teal-700">
                  Repeats {task.recurrence}{task.recurrence_end_date ? ` until ${task.recurrence_end_date}` : ""}
                </span>
              )}
            </div>
            {task.parent_id && task.parent_task_name && (
              <div data-testid="task-parent-breadcrumb" className="text-xs text-slate-500 mb-1 truncate">
                Parent · <button onClick={() => nav(`/tasks/${task.parent_id}`)} className="text-slate-700 hover:text-slate-900 hover:underline font-medium">{task.parent_task_name}</button>
              </div>
            )}
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-testid="task-detail-name">{task.name}</h1>
            {task.source_inquiry && <Link to={`/inquiries/${task.source_inquiry.id}`} data-testid="task-source-inquiry" className="inline-block mt-1 text-xs text-blue-700 hover:underline">From inquiry {task.source_inquiry.number} · {task.source_inquiry.subject}</Link>}
            {task.status_reason && (
              <div className="mt-3 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-3 py-2">
                <span className="font-medium">Reason:</span> {task.status_reason}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
              <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Client</div><div className="font-medium">{task.client_name || "—"}</div></div>
              <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Entity</div><div className="font-medium">{task.entity || "—"}</div></div>
              <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Assignee</div><div className="font-medium">{task.assignee_name || "—"}</div></div>
              <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Due</div><div className="font-medium mono">{task.due_date || "—"}</div></div>
              <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Task date</div><div className="font-medium mono">{task.task_date} {task.task_time || ""}</div></div>
              <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Creator</div><div className="font-medium">{task.creator_name}</div></div>
              <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Created</div><div className="font-medium mono">{task.created_at?.slice(0, 10)}</div></div>
              <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">First opened</div><div className="font-medium mono">{task.first_opened_at ? task.first_opened_at.slice(0, 16).replace("T", " ") : "—"}</div></div>
            </div>
            {task.description && <div className="mt-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">Description</div><div className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{task.description}</div></div>}
            {task.notes && <div className="mt-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Notes</div><div className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{task.notes}</div></div>}

            {task.is_active && (
              <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-slate-100">
                <Button data-testid="task-action-awaiting" size="sm" variant="outline" onClick={() => setActionOpen("awaiting")}>Awaiting</Button>
                <Button data-testid="task-action-hold" size="sm" variant="outline" onClick={() => setActionOpen("on-hold")}><PauseCircle className="w-3.5 h-3.5 mr-1" /> On-Hold</Button>
                <Button data-testid="task-action-done" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setActionOpen("done")}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Done</Button>
              </div>
            )}
          </div>

          {/* Sub-tasks */}
          {task.depth < 2 && (
            <div className="bg-white border border-slate-200 rounded-md">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                <div className="text-sm font-semibold">{task.depth === 0 ? "Sub-tasks" : "Sub-sub-tasks"}</div>
                <Button data-testid="add-subtask-btn" size="sm" variant="outline" onClick={() => setSubOpen(true)}><Plus className="w-4 h-4 mr-1" /> Add</Button>
              </div>
              {/* Rollup summary strip (spec §4) */}
              {task.subtask_summary && task.subtask_summary.total > 0 && (
                <div data-testid="subtask-summary-strip" className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-slate-500 uppercase tracking-wider text-[10px]">Summary</span>
                  {task.subtask_summary.overdue > 0 && (
                    <span data-testid="summary-overdue" className="px-2 py-0.5 rounded-sm border bg-rose-50 text-rose-700 border-rose-200">
                      <b>{task.subtask_summary.overdue}</b> overdue
                    </span>
                  )}
                  {task.subtask_summary.in_progress > 0 && (
                    <span className="px-2 py-0.5 rounded-sm border bg-amber-50 text-amber-800 border-amber-200"><b>{task.subtask_summary.in_progress}</b> in progress</span>
                  )}
                  {task.subtask_summary.pending > 0 && (
                    <span className="px-2 py-0.5 rounded-sm border bg-slate-100 text-slate-700 border-slate-200"><b>{task.subtask_summary.pending}</b> pending</span>
                  )}
                  {task.subtask_summary.awaiting > 0 && (
                    <span className="px-2 py-0.5 rounded-sm border bg-violet-50 text-violet-700 border-violet-200"><b>{task.subtask_summary.awaiting}</b> awaiting</span>
                  )}
                  {task.subtask_summary.on_hold > 0 && (
                    <span className="px-2 py-0.5 rounded-sm border bg-orange-50 text-orange-700 border-orange-200"><b>{task.subtask_summary.on_hold}</b> on-hold</span>
                  )}
                  {task.subtask_summary.completed > 0 && (
                    <span className="px-2 py-0.5 rounded-sm border bg-emerald-50 text-emerald-700 border-emerald-200"><b>{task.subtask_summary.completed}</b> completed</span>
                  )}
                  <span className="ml-auto text-slate-500">of {task.subtask_summary.total}</span>
                </div>
              )}
              {subtasks.length === 0 ? <div className="p-5 text-xs text-slate-500 text-center">No sub-tasks yet.</div> : (
                <ul className="divide-y divide-slate-200">
                  {subtasks.map((s) => {
                    const today = new Date().toISOString().slice(0, 10);
                    const isOverdue = s.is_active !== false && s.due_date && s.due_date < today;
                    return (
                      <li key={s.id} data-testid={`subtask-row-${s.id}`} onClick={() => nav(`/tasks/${s.id}`)} className="px-5 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{s.name}</div>
                          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <span>{s.assignee_name || "Unassigned"}</span>
                            <span>·</span>
                            <span className="mono">Due {s.due_date || "—"}</span>
                            {isOverdue && (
                              <span data-testid={`subtask-overdue-${s.id}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-rose-50 text-rose-700 border border-rose-200">Overdue</span>
                            )}
                            {s.subtask_count > 0 && (
                              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">{s.subtask_count} sub</span>
                            )}
                          </div>
                        </div>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${STATUS_STYLES[s.status]}`}>{s.status.replace("-", " ")}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Comments */}
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold">Comments</div>
            <ul className="divide-y divide-slate-200">
              {comments.length === 0 ? <li className="p-5 text-xs text-slate-500 text-center">No comments yet.</li> :
                comments.map((c) => (
                  <li key={c.id} data-testid={`comment-${c.id}`} className="px-5 py-3">
                    <div className="text-xs text-slate-500"><b className="text-slate-800">{c.user_name}</b> · {c.created_at?.slice(0, 16).replace("T", " ")}</div>
                    <div className="text-sm mt-1 whitespace-pre-wrap">{c.message}</div>
                  </li>
                ))}
            </ul>
            <div className="border-t border-slate-200 p-3 flex gap-2">
              <Input data-testid="comment-input" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Reply to this task…" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendComment(); }}} />
              <Button data-testid="comment-submit" onClick={sendComment} className="bg-blue-700 text-white gap-1"><Send className="w-4 h-4" /> Send</Button>
            </div>
          </div>

          {/* Timeline — big-event activity log */}
          <div className="bg-white border border-slate-200 rounded-md" data-testid="timeline-panel">
            <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold flex items-center gap-2">
              Timeline <span className="text-xs text-slate-400 font-normal">· who did what</span>
            </div>
            {timeline.length === 0 ? (
              <div className="p-5 text-xs text-slate-500 text-center">No activity logged yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {timeline.map((ev) => (
                  <TimelineRow key={ev.id} ev={ev} />
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Timer */}
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="timer-panel">
            <div className="text-sm font-semibold flex items-center gap-2 mb-3"><Timer className="w-4 h-4" /> Time tracker</div>

            {isAnotherRunning ? (
              // CASE B — Another task/sub-task is running for this user
              <div data-testid="timer-blocked-panel" className="space-y-3">
                <div className="text-[11px] uppercase tracking-wider text-amber-700">Another task is currently running</div>
                <div className="text-sm font-medium text-slate-800">
                  {activeSession.parent_task_name ? `${activeSession.parent_task_name} › ` : ""}
                  {activeSession.task_name}
                </div>
                <Button
                  data-testid="timer-go-to-active"
                  variant="outline"
                  onClick={goToActiveTask}
                  className="w-full gap-2 border-amber-200 text-amber-800 hover:bg-amber-50"
                >
                  Go to active task
                </Button>
                <Button data-testid="timer-toggle" disabled className="w-full gap-2 bg-slate-200 text-slate-500 cursor-not-allowed">
                  <Play className="w-4 h-4" /> Start
                </Button>
                <div className="text-[11px] text-slate-500 text-center">Stop the running task before starting another.</div>
              </div>
            ) : isPausedForSegment ? (
              // Paused for a break/meeting/call that will resume THIS task on end.
              <div className="space-y-3">
                <div className="text-[11px] uppercase tracking-wider text-amber-700">Paused — you're on a {activeSession.kind}</div>
                <div data-testid="timer-display" className="mono text-3xl font-semibold text-center py-3">{fmtDuration(task.total_seconds || 0)}</div>
                <Button
                  data-testid="timer-end-segment"
                  onClick={openEndSegmentModal}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                >
                  <Play className="w-4 h-4" /> End {activeSession.kind} & resume
                </Button>
              </div>
            ) : (
              // CASE A — this task is running, or CASE C — no running task
              <>
                <div data-testid="timer-display" className="mono text-3xl font-semibold text-center py-3">{fmtDuration(liveSeconds())}</div>
                <Button
                  data-testid="timer-toggle"
                  onClick={toggleTimer}
                  disabled={!task.is_active}
                  className={`w-full ${task.timer_started_at ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white gap-2`}
                >
                  {task.timer_started_at ? <><Pause className="w-4 h-4" /> Stop</> : <><Play className="w-4 h-4" /> Start</>}
                </Button>
                <div className="text-[11px] text-slate-500 text-center mt-2">Total accumulated across sessions</div>
              </>
            )}
          </div>

          {/* Attachments */}
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-sm font-semibold flex items-center gap-2"><Paperclip className="w-4 h-4" /> Files</div>
              <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="task-file-input" />
              <Button data-testid="task-upload-btn" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>Upload</Button>
            </div>
            {attachments.length === 0 ? <div className="p-4 text-xs text-slate-500 text-center">No files attached.</div> : (
              <ul className="divide-y divide-slate-200">
                {attachments.map((a) => (
                  <li key={a.id} data-testid={`att-row-${a.id}`} className="px-4 py-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium truncate">{a.filename}</div>
                      <div className="text-[10px] text-slate-500 mono">{(a.size / 1024).toFixed(1)} KB</div>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => downloadAtt(a)}><Download className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => deleteAtt(a.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Action dialog */}
      <Dialog open={!!actionOpen} onOpenChange={(o) => { if (!o) { setActionOpen(null); setReason(""); }}}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Mark as {actionOpen}</DialogTitle></DialogHeader>
          {(actionOpen === "awaiting" || actionOpen === "on-hold") && (
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Reason (required)</label>
              <textarea data-testid="action-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1 w-full text-sm border border-slate-200 rounded-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400" />
            </div>
          )}
          {actionOpen === "done" && <div className="text-sm text-slate-600">Marking this task as Done will move it to the completed archive and stop the timer.</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionOpen(null); setReason(""); }}>Cancel</Button>
            <Button data-testid="action-submit" onClick={submitAction} className="bg-blue-700 hover:bg-blue-800 text-white">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NewTaskDialog open={subOpen} onOpenChange={setSubOpen} assignables={assignables} types={types} services={services} entities={entities} currentUserId={user?.id} parentTaskId={task.id} parentTask={task} onCreated={load} />

      {/* Edit dialog — reuses NewTaskDialog in edit mode */}
      <NewTaskDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        assignables={assignables}
        types={types}
        services={services}
        entities={entities}
        currentUserId={user?.id}
        editTask={task}
        onCreated={load}
      />

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onOpenChange={(o) => { if (!deleting) setDeleteOpen(o); }}>        <DialogContent data-testid="task-delete-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete task?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600">
            <p><b>{task.name}</b> will be permanently removed.</p>
            {task.subtask_count > 0 && (
              <p className="mt-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                Heads up — this will also delete <b>{task.subtask_count}</b> sub-task{task.subtask_count === 1 ? "" : "s"} nested underneath.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
            <Button
              data-testid="task-delete-confirm-btn"
              onClick={deleteTask}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleting ? "Deleting…" : "Delete task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
